import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Clock, 
  CheckCircle, 
  ThumbsUp, 
  RefreshCw,
  MessageSquare,
  User,
  Calendar,
  FileText,
  Hourglass
} from 'lucide-react';
import './BrandFeedbackModal.css';
import VideoSelectionModal from './VideoSelectionModal';
import RevisionRequestModal from './RevisionRequestModal';
import SubmissionService from '../services/submissionService';
import SubmissionVideoPlayer from './SubmissionVideoPlayer';
import { addBusinessDays, REVISION_DEADLINE_BUSINESS_DAYS } from '../utils/dateUtils';
import { formatVideoTime, parseVideoTimestamp } from '../utils/videoPlayerUtils';
import toast from 'react-hot-toast';

const OriginalSubmissionModal = ({ 
  isOpen, 
  onClose, 
  submission, 
  onApprove,
  onRequestRevision
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState(0);
  const [showVideoSelection, setShowVideoSelection] = useState(false);
  const [showRevisionRequest, setShowRevisionRequest] = useState(false);
  const [selectedVideosForRevision, setSelectedVideosForRevision] = useState([]);
  const [showSafeZone, setShowSafeZone] = useState(false);
  
  const playerRef = useRef(null);

  // Initialize modal state
  useEffect(() => {
    if (isOpen) {
      setShowApprovalConfirm(false);
      setActiveVideoTab(0);
    }
  }, [isOpen, submission]);

  // Seek video to specific timestamp (from creator notes)
  const seekToTimestamp = (timestamp) => {
    const timeInSeconds = parseVideoTimestamp(timestamp);
    const wasPlaying = playerRef.current?.isPlaying?.();
    playerRef.current?.seekTo(timeInSeconds);
    if (!wasPlaying) {
      setTimeout(() => playerRef.current?.seekTo(timeInSeconds), 50);
    }
  };

  const handleApprove = () => {
    setShowApprovalConfirm(true);
  };

  const confirmApproval = async () => {
    setIsSubmitting(true);
    try {
      const originalData = submission._originalData;
      
      if (!originalData) {
        throw new Error('Submission data not found');
      }

      // Approve all videos in the submission
      await SubmissionService.approveAllVideos(
        originalData.campaignId,
        originalData.briefId,
        submission.id
      );

      toast.success('Content approved successfully!');
      
      if (onApprove) onApprove(submission.id);
      onClose();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve content: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestRevision = () => {
    // Go directly to revision request modal (skip confirmation modal)
    // This matches BrandFeedbackModal behavior
    if (submission.content.length === 1) {
      setSelectedVideosForRevision([submission.content[0]]);
      setShowRevisionRequest(true);
    } else {
      // Multiple videos - show selection modal
      setShowVideoSelection(true);
    }
  };

  const handleVideoSelectionConfirm = (selectedVideos) => {
    setSelectedVideosForRevision(selectedVideos);
    setShowVideoSelection(false);
    setShowRevisionRequest(true);
  };

  const handleRevisionSubmit = async (revisionRequest) => {
    // Don't submit immediately - show confirmation modal first
    // Close the revision request modal
    setShowRevisionRequest(false);
    
    // Pass the revision request data to parent to show confirmation modal
    if (onRequestRevision) {
      onRequestRevision(submission, revisionRequest);
    }
  };

  if (!isOpen || !submission) return null;

  // Get the current video's URL
  const getCurrentVideoUrl = () => {
    const video = submission.content[activeVideoTab];
    return video?.downloadURL || video?.videoUrl;
  };

  return (
    <div className="brand-feedback-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="brand-feedback-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="brand-feedback-modal-header">
          <div className="brand-feedback-modal-title-section">
            <div className="brand-feedback-creator-info">
              <div className="brand-feedback-creator-avatar">
                <img 
                  src={submission.creator.avatar} 
                  alt={submission.creator.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="brand-feedback-avatar-fallback">
                  {submission.creator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
              </div>
              <div className="brand-feedback-creator-details">
                <h3 className="brand-feedback-modal-title">Review Content Submission</h3>
                <p className="brand-feedback-modal-subtitle">
                  {submission.creator.name} - {submission.campaign}
                </p>
              </div>
            </div>
          </div>
          <button className="brand-feedback-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="brand-feedback-modal-content">
          <div className="brand-feedback-review-section">
            {/* Video Preview */}
            <div className="brand-feedback-video-section">
              {/* Video Selectors */}
              <div className="brand-feedback-selectors-row">
                <div className="brand-feedback-video-selector">
                  <label className="brand-feedback-select-label">Video</label>
                  <select 
                    value={activeVideoTab} 
                    onChange={(e) => setActiveVideoTab(parseInt(e.target.value))}
                    className="brand-feedback-video-dropdown"
                  >
                    {submission.content
                      .filter(video => !video.variationType)
                      .map((video) => {
                        const originalIndex = submission.content.findIndex(v => v.id === video.id);
                        return (
                          <option key={video.id} value={originalIndex}>
                            {video.title}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* Variation Selector */}
                {(() => {
                  const currentVideo = submission.content[activeVideoTab];
                  const hasVariations = submission.content.some(video => 
                    video.type === currentVideo.type && 
                    video.variationType && 
                    video.id !== currentVideo.id
                  );
                  
                  if (!hasVariations) return null;
                  
                  const sameTypeVideos = submission.content.filter(video => 
                    video.type === currentVideo.type
                  );
                  
                  return (
                    <div className="brand-feedback-video-selector">
                      <label className="brand-feedback-select-label">Variation</label>
                      <select 
                        value={activeVideoTab} 
                        onChange={(e) => setActiveVideoTab(parseInt(e.target.value))}
                        className="brand-feedback-video-dropdown"
                      >
                        {sameTypeVideos.map((video) => {
                          const originalIndex = submission.content.findIndex(v => v.id === video.id);
                          return (
                            <option key={video.id} value={originalIndex}>
                              {video.title}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })()}
              </div>
              
              <SubmissionVideoPlayer
                ref={playerRef}
                video={submission.content[activeVideoTab]}
                videoUrl={getCurrentVideoUrl()}
                showSafeZone={showSafeZone}
              />
              <div className="brand-feedback-video-controls-row">
                <label className="brand-feedback-safe-zone-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showSafeZone}
                    onChange={(e) => setShowSafeZone(e.target.checked)}
                  />
                  <span className="brand-feedback-toggle-slider"></span>
                  <span className="brand-feedback-toggle-label">Show Safe Zone</span>
                </label>
              </div>
            </div>

            {/* Video-Specific Notes */}
            <div className="brand-feedback-notes-section">
              <h4 className="brand-feedback-section-title">Creator Notes - {submission.content[activeVideoTab].title}</h4>
              
              {submission.content[activeVideoTab].notes && (
                <div className="brand-feedback-creator-note">
                  <div className="brand-feedback-note-header">
                    <FileText size={16} />
                    <span>Video Notes</span>
                  </div>
                  <p className="brand-feedback-note-content">{submission.content[activeVideoTab].notes}</p>
                </div>
              )}

              {submission.content[activeVideoTab].timestampedNotes && submission.content[activeVideoTab].timestampedNotes.length > 0 && (
                <div className="brand-feedback-timestamped-notes">
                  <div className="brand-feedback-note-header">
                    <Clock size={16} />
                    <span>Timestamped Notes</span>
                  </div>
                  <div className="brand-feedback-timestamp-list">
                    {submission.content[activeVideoTab].timestampedNotes.map((note, index) => (
                      <div 
                        key={index} 
                        className="brand-feedback-timestamp-item"
                        onClick={() => seekToTimestamp(note.timestamp || note.time)}
                        style={{ cursor: 'pointer' }}
                        title="Click to seek to this timestamp"
                      >
                        <div className="brand-feedback-timestamp-time">
                          {note.timestamp || (note.time ? formatVideoTime(note.time) : '0:00')}
                        </div>
                        <div className="brand-feedback-timestamp-content">{note.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission Details */}
              <div className="brand-feedback-submission-details">
                <div className="brand-feedback-detail-item">
                  <User size={16} />
                  <span><strong>Creator:</strong> {submission.creator.name} ({submission.creator.handle})</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <Calendar size={16} />
                  <span><strong>Submitted:</strong> {new Date(submission.submittedAt).toLocaleDateString()}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <MessageSquare size={16} />
                  <span><strong>Campaign:</strong> {submission.campaign}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <RefreshCw size={16} />
                  <span><strong>Revisions:</strong> {submission.revisionCount || 0}/2</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="brand-feedback-modal-footer">
          {submission.status === 'needs_revision' ? (
            <div className="brand-feedback-awaiting-revision">
              <div className="brand-feedback-awaiting-revision-content">
                <Hourglass size={20} className="brand-feedback-awaiting-icon" />
                <div className="brand-feedback-awaiting-text">
                  <h4>
                    Awaiting Revision {((submission.revisionCount || submission._originalData?.revisionCount || 0) + 1)} of 2
                  </h4>
                  <p>
                    {(() => {
                      const revisionDate = submission.lastRevisionRequestedAt
                        || submission._originalData?.lastRevisionRequestedAt
                        || submission.submittedAt;
                      const revisionDateObj = revisionDate instanceof Date 
                        ? revisionDate 
                        : revisionDate?.toDate 
                        ? revisionDate.toDate() 
                        : new Date(revisionDate);
                      
                      const deadline = addBusinessDays(new Date(revisionDateObj), REVISION_DEADLINE_BUSINESS_DAYS);
                      const now = new Date();
                      const timeLeft = deadline.getTime() - now.getTime();
                      const hoursLeft = Math.ceil(timeLeft / (1000 * 60 * 60));
                      const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
                      
                      if (timeLeft <= 0) {
                        return 'Creator deadline has passed';
                      } else if (hoursLeft < 24) {
                        return `Time left: ${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'}`;
                      } else {
                        return `Time left: ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`;
                      }
                    })()}
                  </p>
                </div>
              </div>
              <button 
                className="brand-feedback-cancel-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <div className="brand-feedback-modal-actions">
              <button 
                className="brand-feedback-cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              
              <div className="brand-feedback-action-buttons">
                <button 
                  className="brand-feedback-approve-btn"
                  onClick={handleApprove}
                  disabled={isSubmitting}
                >
                  <ThumbsUp size={16} />
                  Approve
                </button>
                
                <button 
                  className="brand-feedback-revision-btn"
                  onClick={handleRequestRevision}
                  disabled={isSubmitting}
                >
                  <RefreshCw size={16} />
                  Request Revision
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalConfirm && (
        <div className="brand-feedback-confirm-overlay">
          <div className="brand-feedback-confirm-modal">
            <div className="brand-feedback-confirm-header">
              <h3>Confirm Approval</h3>
              <button onClick={() => setShowApprovalConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="brand-feedback-confirm-content">
              <p>Are you sure you want to approve this submission?</p>
              <p className="brand-feedback-confirm-subtitle">
                This action will mark the content as approved and notify the creator.
              </p>
            </div>
            <div className="brand-feedback-confirm-actions">
              <button 
                className="brand-feedback-confirm-cancel"
                onClick={() => setShowApprovalConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="brand-feedback-confirm-approve"
                onClick={confirmApproval}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Yes, Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Selection Modal */}
      <VideoSelectionModal
        isOpen={showVideoSelection}
        onClose={() => setShowVideoSelection(false)}
        submission={submission}
        onConfirm={handleVideoSelectionConfirm}
      />

      {/* Revision Request Modal */}
      <RevisionRequestModal
        isOpen={showRevisionRequest}
        onClose={() => setShowRevisionRequest(false)}
        submission={submission}
        selectedVideos={selectedVideosForRevision}
        onSubmit={handleRevisionSubmit}
      />
    </div>
  );
};

export default OriginalSubmissionModal;

